import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

import { AppState } from 'types/app';
import { SephoraLoyaltyResponse, SephoraUser } from 'types/sephora';
import {
  createSephoraAccountLink,
  getSephoraLoyaltyLink,
  registerSephoraUser,
  removeSephoraAccountLink,
  validateSephoraAccountLink
} from 'apis/mafia';
import { RECEIVE_SEPHORA_ERROR, RECEIVE_SEPHORA_LOYALTY, REQUEST_SEPHORA_LOYALTY } from 'constants/reduxActions';
import { trackError } from 'helpers/ErrorUtils';
import { fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import { selectMafiaConfig } from 'selectors/environment';
import { SEPHORA_INVALID_OTP } from 'constants/sephora';

export function createSephoraUser(userData: SephoraUser): ThunkAction<Promise<void>, AppState, void, AnyAction> {
  return (dispatch, getState) => {
    const state = getState();
    const { cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);

    dispatch(requestSephoraLoyalty({ showLoader: true }));

    return registerSephoraUser(userData, mafiaConfig, cookies).then(async (res: Response) => {
      if (res.status === 400) {
        const { message } = await res.json();
        dispatch(receiveSephoraError(message));
        trackError('NON-FATAL', 'Failed to register Beauty Insider account', { message });
      }

      dispatch(fetchSephoraLoyalty({ showLoader: true }));
    });
  };
}

export function linkSephoraAccount(customerEmail: string): ThunkAction<Promise<void>, AppState, void, AnyAction> {
  return (dispatch, getState) => {
    const state = getState();
    const { cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);

    dispatch(requestSephoraLoyalty({ showLoader: true }));

    return createSephoraAccountLink(customerEmail, mafiaConfig, cookies)
      .then(async (res: Response) => {
        if (res.ok) {
          dispatch(fetchSephoraLoyalty({ showLoader: true }));
        }

        if (res.status > 400) return fetchErrorMiddleware(res);

        if (res.status === 400) {
          const { message } = await res.json();
          dispatch(receiveSephoraError(message));
        }
      })
      .catch((err: Error) => {
        dispatch(receiveSephoraError('Failed to link Beauty Insider account. Please try again.'));
        trackError('NON-FATAL', 'Failed to link Beauty Insider account', err);
      });
  };
}

export function validateSephoraAccount(customerEmail: string, otpCode: string): ThunkAction<Promise<void>, AppState, void, AnyAction> {
  return (dispatch, getState) => {
    const state = getState();
    const { cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);

    dispatch(requestSephoraLoyalty({ showLoader: true }));

    return validateSephoraAccountLink(customerEmail, otpCode, mafiaConfig, cookies)
      .then(async (res: Response) => {
        if (res.ok) {
          dispatch(fetchSephoraLoyalty({ showLoader: true }));
        }

        if (res.status > 400) return fetchErrorMiddleware(res);

        if (res.status === 400) {
          const { message } = await res.json();

          if (message === SEPHORA_INVALID_OTP) {
            dispatch(receiveSephoraError('Invalid code. Please check your code and try again.'));
          } else {
            dispatch(receiveSephoraError('Failed to verify Beauty Insider account. Please try again.'));
          }
        }
      })
      .catch((err: Error) => {
        dispatch(receiveSephoraError('Failed to verify Beauty Insider account. Please try again.'));
        trackError('NON-FATAL', 'Failed to verify Beauty Insider account', err);
      });
  };
}

export function fetchSephoraLoyalty({ showLoader }: { showLoader: boolean }): ThunkAction<Promise<void>, AppState, void, AnyAction> {
  return (dispatch, getState) => {
    const state = getState();
    const { cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);

    dispatch(requestSephoraLoyalty({ showLoader }));

    return getSephoraLoyaltyLink(mafiaConfig, cookies)
      .then(async (res: Response) => {
        const data = await res.json();
        dispatch(receiveSephoraError(''));
        dispatch(receiveSephoraLoyalty(data));
      })
      .catch((err: Error) => {
        dispatch(receiveSephoraError('Failed to load Beauty Insider account. Please try again.'));
        trackError('NON-FATAL', 'Failed to load Beauty Insider account', err);
      });
  };
}

export function unlinkSephoraAccount(): ThunkAction<Promise<void>, AppState, void, AnyAction> {
  return (dispatch, getState) => {
    const state = getState();
    const { cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);

    dispatch(requestSephoraLoyalty({ showLoader: true }));

    return removeSephoraAccountLink(mafiaConfig, cookies)
      .then(fetchErrorMiddleware)
      .then(() => {
        dispatch(receiveSephoraLoyalty({}));
      })
      .catch((err: Error) => {
        dispatch(receiveSephoraError('Failed to unlink Beauty Insider account. Please try again.'));
        trackError('NON-FATAL', 'Failed to unlink Beauty Insider account', err);
      });
  };
}

export const requestSephoraLoyalty = ({ showLoader }: { showLoader: boolean }) => ({ type: REQUEST_SEPHORA_LOYALTY, showLoader } as const);

export const receiveSephoraLoyalty = (loyaltyData: Partial<SephoraLoyaltyResponse>) =>
  ({
    type: RECEIVE_SEPHORA_LOYALTY,
    ...loyaltyData
  } as const);

export const receiveSephoraError = (message: string) =>
  ({
    type: RECEIVE_SEPHORA_ERROR,
    message
  } as const);

export type SephoraAction =
  | ReturnType<typeof requestSephoraLoyalty>
  | ReturnType<typeof receiveSephoraLoyalty>
  | ReturnType<typeof receiveSephoraError>;
